<template>
  <div>
    <div
      id="preEnrichmentModal"
      class="modal fade d-none"
      :class="{ 'show d-block': value }"
      tabindex="-1"
      aria-labelledby="enrichmentTitle"
      aria-modal="true"
      role="dialog"
    >
      <div class="modal-dialog modal-sm modal-dialog-centered">
        <form @submit.prevent="submit" class="modal-content" autocomplete="off">
          <div class="modal-header d-flex flex-column align-items-center py-3 px-4">
            <h4 class="text-center text-accent" id="enrichmentTitle">
              {{ parameters.enrichmentTitle || $t('preEnrichment.title') }}
            </h4>
            <small class="d-block text-center text-muted">{{ $t('preEnrichment.subtitle') }}</small>
          </div>

          <div class="modal-body px-4 pb-0" style="overflow-y: auto;">
            <div class="form-group" v-if="parameters.showNameEnrichment">
              <small class="text-accent d-block" :class="{ 'text-dark': whiteMode }">{{
                $t('enrichment.labelName')
              }}</small>
              <input
                id="enrichmentName"
                :aria-label="$t('enrichment.labelName')"
                v-model="name"
                class="text-success border-top-0 border-left-0 border-right-0 form-control px-2"
                :placeholder="$tc('enrichment.PlaceholderName')"
                ref="enrichmentName"
                required
              />
            </div>

            <div class="form-group" v-if="parameters.showEmailEnrichment">
              <small class="text-accent d-block" :class="{ 'text-dark': whiteMode }">{{
                $t('enrichment.labelEmail')
              }}</small>
              <input
                id="enrichmentEmail"
                :aria-label="$t('enrichment.labelEmail')"
                type="email"
                v-model="email"
                :class="{ 'text-danger': errorMsgEmail !== '' }"
                class="text-success border-top-0 border-left-0 border-right-0 form-control px-2"
                placeholder="email@email.com"
                ref="enrichmentEmail"
                required
              />
              <small
                v-if="errorMsgEmail"
                class="form-text text-danger d-flex align-items-center fade"
                :class="{ show: errorMsgEmail !== '' }"
              >
                <img v-show="errorMsgEmail !== ''" class="mr-2" src="/images/close.svg" />
                {{ errorMsgEmail }}</small
              >
            </div>

            <div class="form-group" v-if="parameters.showGenderEnrichment">
              <small class="text-accent d-block" :class="{ 'text-dark': whiteMode }">{{
                $t('enrichment.labelGender')
              }}</small>
              <select
                id="enrichmentGender"
                :aria-label="$t('enrichment.labelGender')"
                v-model="gender"
                class="text-success border-top-0 border-left-0 border-right-0 form-control px-2"
                ref="enrichmentGender"
                required
              >
                <option value="m">{{ $t('enrichment.maleGender') }}</option>
                <option value="f">{{ $t('enrichment.femaleGender') }}</option>
                <option value="o">{{ $t('enrichment.otherGender') }}</option>
              </select>
            </div>

            <div id="enrichmentDocumentFormGroup" class="form-group" v-if="showDocumentInput">
              <small class="text-accent d-block" :class="{ 'text-dark': whiteMode }">{{
                $t(labelDocumentInput)
              }}</small>
              <the-mask
                id="enrichmentDocument"
                :aria-label="$t(labelDocumentInput)"
                type="tel"
                class="text-success border-top-0 border-left-0 border-right-0 form-control px-2"
                ref="document"
                :placeholder="maskPlaceholderInput"
                :mask="maskDocumentInput"
                v-model="document"
                required
                :class="{ 'text-danger': errorMsgDocument !== '' }"
                @input="errorMsgDocument = ''"
              ></the-mask>
              <small
                v-if="errorMsgDocument"
                class="form-text text-danger mt-2 d-flex align-items-center fade"
                :class="{ show: errorMsgDocument !== '' }"
              >
                <img v-show="errorMsgDocument !== ''" class="mr-2" src="/images/close.svg" />
                {{ errorMsgDocument }}</small
              >
            </div>

            <div v-if="parameters.showAddressEnrichment">
              <div class="form-group">
                <small class="text-accent d-block" :class="{ 'text-dark': whiteMode }">{{
                  $t('enrichment.zipcode')
                }}</small>
                <input
                  id="enrichmentAddressZipCode"
                  :aria-label="$t('enrichment.zipcode')"
                  class="text-success border-top-0 border-left-0 border-right-0 form-control px-2"
                  placeholder="00000-000"
                  v-mask="'#####-###'"
                  v-model="zipcode"
                  required
                />
                <small
                  v-if="errorMsgZipCode"
                  class="form-text text-danger mt-2 d-flex align-items-center fade"
                  :class="{ show: errorMsgZipCode !== '' }"
                >
                  <img v-show="errorMsgZipCode !== ''" class="mr-2" src="/images/close.svg" />
                  {{ errorMsgZipCode }}</small
                >
              </div>

              <div class="form-group">
                <small class="text-accent d-block" :class="{ 'text-dark': whiteMode }">{{
                  $t('enrichment.state')
                }}</small>
                <input
                  id="enrichmentAddressState"
                  :aria-label="$t('enrichment.state')"
                  class="text-success border-top-0 border-left-0 border-right-0 form-control px-2"
                  :placeholder="$tc('enrichment.statePlaceholder')"
                  v-model="state"
                  required
                />
              </div>

              <div class="form-group">
                <small class="text-accent d-block" :class="{ 'text-dark': whiteMode }">{{
                  $t('enrichment.city')
                }}</small>
                <input
                  id="enrichmentAddressCity"
                  :aria-label="$t('enrichment.city')"
                  class="text-success border-top-0 border-left-0 border-right-0 form-control px-2"
                  :placeholder="$tc('enrichment.cityPlaceholder')"
                  v-model="city"
                  required
                />
              </div>

              <div class="form-group">
                <small class="text-accent d-block" :class="{ 'text-dark': whiteMode }">{{
                  $t('enrichment.neighbor')
                }}</small>
                <input
                  id="enrichmentAddressNeighbor"
                  :aria-label="$t('enrichment.neighbor')"
                  class="text-success border-top-0 border-left-0 border-right-0 form-control px-2"
                  :placeholder="$tc('enrichment.neighborPlaceholder')"
                  v-model="neighbor"
                  required
                />
              </div>

              <div class="form-group">
                <small class="text-accent d-block" :class="{ 'text-dark': whiteMode }">{{
                  $t('enrichment.address')
                }}</small>
                <input
                  id="enrichmentAddress"
                  :aria-label="$t('enrichment.address')"
                  class="text-success border-top-0 border-left-0 border-right-0 form-control px-2"
                  :placeholder="$tc('enrichment.addressPlaceholder')"
                  v-model="address"
                  required
                />
              </div>

              <div class="form-group">
                <small class="text-accent d-block" :class="{ 'text-dark': whiteMode }">{{
                  $t('enrichment.number')
                }}</small>
                <input
                  id="enrichmentAddressNumber"
                  :aria-label="$t('enrichment.number')"
                  class="text-success border-top-0 border-left-0 border-right-0 form-control px-2"
                  placeholder="00"
                  v-model="number"
                  required
                  ref="inputNumber"
                />
              </div>

              <div class="form-group">
                <small class="text-accent d-block" :class="{ 'text-dark': whiteMode }">{{
                  $t('enrichment.complement')
                }}</small>
                <input
                  id="enrichmentAddressComplement"
                  :aria-label="$t('enrichment.complement')"
                  class="text-success border-top-0 border-left-0 border-right-0 form-control px-2"
                  v-model="complement"
                />
              </div>
            </div>

            <div class="form-group" v-if="parameters.showPhoneEnrichment">
              <small class="text-accent d-block" :class="{ 'text-dark': whiteMode }">{{
                $t('enrichment.labelPhone')
              }}</small>

              <InternationalPhone v-model="phone" :required="true" :accessibleLabel="$t('enrichment.labelPhone')" ref="internationalPhone" />
              <small
                v-if="errorMsgPhone"
                class="form-text text-danger mt-2 d-flex align-items-center fade"
                :class="{ show: errorMsgPhone !== '' }"
              >
                <img v-show="errorMsgPhone !== ''" class="mr-2" src="/images/close.svg" />
                {{ errorMsgPhone }}</small
              >
            </div>

            <div class="form-group" v-if="parameters.showBirthEnrichment">
              <small class="text-accent d-block" :class="{ 'text-dark': whiteMode }">{{
                $t('enrichment.labelBirth')
              }}</small>
              <input
                id="enrichmentBirth"
                :aria-label="$t('enrichment.labelBirth')"
                class="text-success border-top-0 border-left-0 border-right-0 form-control px-2"
                placeholder="00/00/0000"
                v-mask="'##/##/####'"
                v-model="birth"
                type="tel"
                required
                :class="{ 'text-danger': errorMsgBirth !== '' }"
              />
              <small
                v-if="errorMsgBirth"
                class="form-text text-danger mt-2 d-flex align-items-center fade"
                :class="{ show: errorMsgBirth !== '' }"
              >
                <img v-show="errorMsgBirth !== ''" class="mr-2" src="/images/close.svg" />
                {{ errorMsgBirth }}</small
              >
            </div>

            <div
              id="agreements"
              v-for="(a, index) in parameters.enrichAgreements"
              :key="index"
              class="form-group form-check"
            >
              <input
                class="form-check-input cursor-pointer"
                type="checkbox"
                v-model="terms"
                :value="{ name: a.name, required: a.required }"
                :id="`checkPreEnrichmentTerms${index}`"
                :required="a.required"
                :aria-labelledby="`checkPreEnrichmentTermsLabel${index}`"
              />
              <label :id="`checkPreEnrichmentTermsLabel${index}`" class="form-check-label cursor-pointer fs-12" :for="`check${index}`">
                <a target="_blank" :href="a.href" v-if="a.href">{{ a.label }}</a>
                <span v-else>{{ a.label }}</span>
              </label>
            </div>
            <div v-if="parameters.enrichDisclaimer" class="form-group">
              <label
                class="form-check-label text-muted fs-12 w-100 text-responsive"
                v-html="parameters.enrichDisclaimer"
              ></label>
            </div>
          </div>
          <div
            class="modal-footer text-center border-0 pb-4 pt-0"
            :class="[parameters.preEnrich ? 'justify-content-center' : 'justify-content-between']"
          >
            <div v-if="!parameters.preEnrich">
              <button type="button" class="btn btn-link text-muted" data-dismiss="modal">
                {{ $t('enrichment.skip') }}
              </button>
            </div>
            <div>
              <button
                id="submitButton"
                type="submit"
                class="btn btn-accent btn-round bg-accent shadow btn-lg"
                :class="{ 'bg-dark': whiteMode }"
                :disabled="loading"
                style="min-width: 132px;"
              >
                {{ $t('enrichment.start') }}
              </button>
            </div>
          </div>

          <PrivacyLinks
            placement="enrichment"
            v-if="parameters.privacyLinks.length > 0"
            :privacyLinks="parameters.privacyLinks"
          />
          <div v-if="this.error" class="text-danger p-4">
            {{ $t('preEnrichment.error') }}
          </div>
        </form>
      </div>
    </div>
    <div id="backdrop" class="modal-backdrop fade show d-none" :class="{ 'd-block': value }"></div>
  </div>
</template>
<script>
import PrivacyLinks from '@/components/PrivacyLinks.vue';
import { validate as validateCPF } from 'gerador-validador-cpf';
import { TheMask, mask } from 'vue-the-mask';
import { isCNPJ } from 'validation-br';
import AddressService from '../services/AddressService';
import InternationalPhone from './InternationalPhone.vue';
export default {
  components: { PrivacyLinks, TheMask, InternationalPhone },
  props: {
    value: { type: Boolean, default: false },
    instance: { type: Object, default: {} },
    parameters: { type: Object, default: {} },
    error: { type: Boolean, default: false },
    loading: { type: Boolean, default: false },
  },
  directives: {
    mask,
  },
  data() {
    return {
      name: '',
      email: '',
      gender: '',
      zipcode: '',
      state: '',
      city: '',
      neighbor: '',
      address: '',
      number: '',
      complement: '',
      phone: '',
      document: '',
      birth: '',
      errorMsgEmail: '',
      errorMsgDocument: '',
      errorMsgZipCode: '',
      errorMsgPhone: '',
      errorMsgBirth: '',
      resolve: null,
      terms: [],
    };
  },
  mounted() {
    // O componente já nasce visível (v-if no pai), então o foco vai para o primeiro campo na montagem.
    this.$nextTick(() => this.$el.querySelector('.modal-body input, .modal-body select')?.focus());
  },
  watch: {
    enrichmentVisibility(val) {
      const vm = this;
      if (!val) {
        vm.resolve(null);
      }
    },
    zipcode(value) {
      if (this.isZipcodeValid(value)) {
        AddressService.getAddressByCEP(value).then((address) => {
          if (!address.erro) {
            this.fillAddressInputs(address);
          }
        });
      }
    },
  },
  computed: {
    isTermsChecked() {
      return this.terms.filter((s) => s.required).length ===
        this.parameters.enrichAgreements.filter((s) => s.required).length
        ? true
        : false;
    },
    whiteMode() {
      if (this.parameters) {
        return this.parameters.color === '#ffffff';
      } else {
        return false;
      }
    },
    showDocumentInput() {
      return this.parameters.showDocumentEnrichment || this.parameters.showEntityDocumentEnrichment;
    },
    maskDocumentInput() {
      const maskDocumentInput = [];
      if (this.parameters.showDocumentEnrichment) maskDocumentInput.push('###.###.###-##');
      if (this.parameters.showEntityDocumentEnrichment) maskDocumentInput.push('XX.XXX.XXX/XXXX-##');
      return maskDocumentInput;
    },
    maskPlaceholderInput() {
      const placeholder = this.maskDocumentInput;
      if (placeholder.length > 1) {
        return '000.000.000-00 (/0000-00)';
      }
      return placeholder[0].replace(/#/g, '0');
    },
    labelDocumentInput() {
      if (this.parameters.showDocumentEnrichment && this.parameters.showEntityDocumentEnrichment) {
        return 'enrichment.labelDocumentOrEntityDocument';
      } else if (this.parameters.showDocumentEnrichment) {
        return 'enrichment.labelDocument';
      }
      return 'enrichment.labelEntityDocument';
    },
  },
  methods: {
    isZipcodeValid(zipcode) {
      return /^[0-9]{5}-[0-9]{3}$/.test(zipcode);
    },
    isButtonEnabled(parameters) {
      if (parameters.showDocumentEnrichment && this.document === '') {
        return true;
      }

      if (parameters.showNameEnrichment && this.name === '') {
        return true;
      }

      if (parameters.showEmailEnrichment && this.email === '') {
        return true;
      }

      if (parameters.showPhoneEnrichment && this.phone === '') {
        return true;
      }

      if (parameters.showBirthEnrichment && this.birth === '') {
        return true;
      }

      if (parameters.enrichAgreements && !this.isTermsChecked) {
        return true;
      }

      return false;
    },
    clearFields() {
      this.name = '';
      this.email = '';
      this.gender = '';
      this.zipcode = '';
      this.state = '';
      this.city = '';
      this.neighbor = '';
      this.address = '';
      this.number = '';
      this.complement = '';
      this.birth = '';
      this.phone = '';
      this.document = '';
      this.errorMsgEmail = '';
      this.errorMsgDocument = '';
      this.errorMsgPhone = '';
      this.errorMsgBirth = '';
    },
    fillAddressInputs(address) {
      this.state = address.uf;
      this.city = address.localidade;
      this.neighbor = address.bairro;
      this.address = address.logradouro;
      this.$refs.inputNumber.focus();
    },
    ensureEnrichAgreementsIsAList() {
      const { enrichAgreements } = this.parameters;
      return !Array.isArray(enrichAgreements) ? Object.values({ ...enrichAgreements }) : enrichAgreements;
    },
    submit() {
      const inputEmail = document.getElementById('enrichmentEmail');
      const inputDocument = document.getElementById('enrichmentDocument');
      const inputBirth = document.getElementById('enrichmentBirth');

      const enrichmentParameters = {};

      if (this.parameters.showNameEnrichment) {
        enrichmentParameters.name = this.name;
      }

      if (this.parameters.showEmailEnrichment) {
        if (inputEmail.checkValidity()) {
          enrichmentParameters.email = this.email;
          this.errorMsgEmail = '';
        } else {
          this.errorMsgEmail = this.$t('enrichment.requiredField');
          return;
        }
      }

      if (this.parameters.showGenderEnrichment) {
        enrichmentParameters.gender = this.gender;
      }

      if (this.parameters.showAddressEnrichment) {
        enrichmentParameters.address = {
          zipcode: this.zipcode.replace('-', ''),
          address: this.address,
          number: this.number,
          complement: this.complement,
          neighbor: this.neighbor,
          city: this.city,
          state: this.state,
        };
      }

      if (this.parameters.showPhoneEnrichment) {
        const phoneComponent = this.$refs.internationalPhone;
        const isPhoneValid = phoneComponent && phoneComponent.validate();
        if (isPhoneValid && this.phone) {
          enrichmentParameters.phone = this.phone;
          this.errorMsgPhone = '';
        } else {
          this.errorMsgPhone = this.$t('enrichment.requiredField');
          return;
        }
      }

      if (this.parameters.showEntityDocumentEnrichment || this.parameters.showDocumentEnrichment) {
        const isCPFValid = this.validateDocument();
        const isCNPJValid = this.validateEntityDocument();

        enrichmentParameters.cpf = this.document;
        this.errorMsgDocument = '';

        if (!isCPFValid && !isCNPJValid) {
          enrichmentParameters.cpf = '';
          this.errorMsgDocument = this.$t('enrichment.invalidDocument');
          this.$refs.document.$el.focus();
          return;
        }
      }

      if (this.parameters.showBirthEnrichment) {
        if (!inputBirth.checkValidity()) {
          this.errorMsgBirth = this.$t('enrichment.requiredField');
          return;
        }

        if (this.birth.length === 10) {
          const [day, month, year] = this.birth.split('/').map(Number);
          const birthdayDate = new Date(year, month, day);
          const hasBorn = new Date(year, month - 1, day);
          const minimumBirthYear = new Date(1910, 0, 1);

          const actualDate = new Date();
          const isBigger = hasBorn.getTime() <= actualDate.getTime();
          const isLess = birthdayDate.getTime() >= minimumBirthYear.getTime();
          if (!isBigger || !isLess) {
            this.errorMsgBirth = this.$t('enrichment.invalidDate');
            return;
          }

          enrichmentParameters.birth = hasBorn;
        }

        this.errorMsgBirth = '';
      }

      if (this.parameters.enrichAgreements) {
        const enrichAgreements = this.ensureEnrichAgreementsIsAList();
        const agreements = enrichAgreements.map((s) => ({
          name: s.name,
          version: s.version,
          text: s.text,
          href: s.href,
        }));

        for (const iterator of agreements) {
          iterator.accept = false;
          for (const t of this.terms) {
            if (iterator.name === t.name) {
              iterator.accept = true;
            }
          }
        }

        enrichmentParameters.consents = agreements;
      }

      if (this.deviceIsIphone()) {
        window.scrollTo(0, 0);
      }

      this.$emit('complete', enrichmentParameters);
    },
    deviceIsIphone() {
      const isIphone = /iPhone/i.test(navigator.userAgent);
      return isIphone;
    },
    validateDocument() {
      return (
        this.parameters.showDocumentEnrichment &&
        validateCPF(this.document) &&
        this.document.length === 11 &&
        this.document.length > 0
      );
    },

    validateEntityDocument() {
      if (!this.parameters.showEntityDocumentEnrichment) return false;
      if (this.document.length !== 14) return false;
      return isCNPJ(this.document);
    },
  },
};
</script>
<style>
.modal-body {
  position: relative;
  overflow-y: auto;
  max-height: 400px;
  padding: 15px;
  scrollbar-width: thin;
}

.modal-body::-webkit-scrollbar {
  width: 5px;
}

.modal-body::-webkit-scrollbar-thumb {
  background-color: #c6c7c9 !important;
}

.error-svg {
  display: inline-block;
  height: 20px;
  width: 20px;
  fill: var(--danger);
}
.btn-round {
  border-radius: 30px;
}
.shadow-primary {
  box-shadow: 0 10px 35px -10px var(--primary);
}
.text-responsive {
  overflow-wrap: break-word;
}
</style>
